import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useTheme } from '../../../theme';
import { AviaTraveller } from '@websky/graphql';
import { collectPassengerInfo } from '../../utils';
import Disabilities from '../Disabilities/Disabilities';
import Radio from '../../../Radio/components/Radio';
import { BaseButton, CopyButton } from '../../../BaseComponents';
import { PassTitle } from '@entities/passengers';

interface Props {
	index: number;
	traveller: AviaTraveller;
	selectedAsAccompanying?: boolean;
	isAccompanied?: boolean;
	isRelinkingMode?: boolean;
	isRelinkingAvailable?: boolean;
	isDisabled?: boolean;
	onSetRelinkingMode?: (passenger: AviaTraveller) => void;
	onLinkTo?: (passengerId: string) => void;
	onChangePassenger?: () => void;
}

const Passenger: FC<Props> = ({
	index,
	traveller,
	selectedAsAccompanying,
	isAccompanied,
	isRelinkingMode,
	isRelinkingAvailable,
	isDisabled,
	onSetRelinkingMode,
	onLinkTo,
	onChangePassenger
}) => {
	const { Passenger: css } = useTheme('PassengersDetails');
	const { t } = useTranslation('OrderCard');

	const { travellerName, mainInfo, additionalInfo, loyaltyInfo, ticketNumbers } = collectPassengerInfo(traveller);

	const handleLinkTo = () => onLinkTo?.(traveller.id);
	const handleSetRelinkingMode = () => onSetRelinkingMode?.(traveller);

	const showRadioButton = Boolean(isRelinkingMode && onLinkTo);
	const showChangeButton = Boolean(!showRadioButton && onChangePassenger);
	const showDisabilities = Boolean(!isRelinkingMode && traveller.disabilities.length);
	const showChangeLinkToAdult = Boolean(isRelinkingAvailable && isAccompanied && onSetRelinkingMode);

	const ticketInfo = useMemo(() => {
		const info: Record<string, React.ReactNode> = {};

		if (!ticketNumbers?.length) {
			return info;
		}

		ticketNumbers.map(
			(number, index) =>
				(info[`${number}-${index}`] = (
					<div>
						{`${t('Ticket number')}: `}

						<CopyButton
							classes={{ root: css.ticket__button_root, endIcon: css.ticket__button_endIcon }}
							variant="text"
							iconPosition="right"
							text={number}
							copyButtonText={number}
							copiedButtonText={number}
						/>
					</div>
				))
		);

		return info;
	}, [ticketNumbers]);

	return (
		<div className={cn(css.passenger, { [css.relink]: isRelinkingMode })}>
			<div className={css.passenger__passTitle}>
				<PassTitle
					classes={{ title: css.passenger__passTitle_title }}
					variant="added"
					title={travellerName}
					icon={index}
					fieldsMap={{
						gender: mainInfo.join(', '),
						document: additionalInfo.join(', '),
						loyalty: loyaltyInfo.join(', '),
						...ticketInfo
					}}
					headerButtonProps={
						showChangeLinkToAdult && {
							children: t('Change link to adult'),
							onClick: handleSetRelinkingMode
						}
					}
				/>

				{showRadioButton && (
					<Radio checked={selectedAsAccompanying} onClick={handleLinkTo} disabled={isDisabled} />
				)}

				{showChangeButton && (
					<BaseButton className={css.change__button} variant="optionEdit" onClick={onChangePassenger}>
						{t('Change')}
					</BaseButton>
				)}
			</div>

			{showDisabilities && <Disabilities disabilities={traveller.disabilities} />}
		</div>
	);
};

export default Passenger;
